/* @flow */

import { useState, useEffect, useMemo } from 'react'
import { differenceInMilliseconds, parseISO, format, addDays, subDays, isAfter } from 'date-fns'
import { flatten, unique } from 'atpjs/helpers/array'

import type CancelablePromise from 'atpjs/cancelable_promise'

type SlotType = {
  holiday: boolean,
  holidayName: ?string,
  holidayCount: ?number,
  firstHoliday: boolean,
  available: boolean,
  start: Date,
  end: Date
}

type DataDateRangeType = {
  start: string,
  end: string
}

type DataTimeRangeType = {
  start: string,
  end: string
}

type DataSlotType = {
  start: string,
  end: string,
  available: boolean
}

type DataSlotsType = Array<DataSlotType>

type DataBranchType = {
  id: number,
  name: string,
  holidays: HolidaysType,
  slots: DataSlotsType
}

type DataBranchesType = Array<DataBranchType>

type DataType = {
  dateRange: DataDateRangeType,
  timeRange: DataTimeRangeType,
  previousStartDate: ?string,
  nextStartDate: ?string,
  branches: DataBranchesType
}

type DirectionType = 'forward' | 'backward'

type GridByBranchOptionsType = {
  displaySlotName: boolean
}

export const formatTime = (date) => {
  return format(date, 'HH:mm')
}

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: null,
    height: null
  })

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowSize
}

const sortByPrimaryBranch = (branches: DataBranchesType) => {
  return branches.sort((branch) => (branch.primary ? -1 : 1))
}

const hasEvents = (data: DataType) => {
  return data.branches.some((branch) => {
    return Object.values(branch.slots).some((slots) => {
      return slots.some((slot) => {
        return slot.available
      })
    })
  })
}

const tryForDays = (data: DataType, originalDate: Date, days: number, direction: DirectionType) => {
  const end = data.dateRange.end
  const dateUntil = direction === 'forward' ? addDays(originalDate, days) : subDays(originalDate, days)
  const endDate = parseISO(end)
  return direction === 'forward' ? !isAfter(endDate, dateUntil) : isAfter(endDate, dateUntil)
}

const getDirection = (date: Date, previousDate: ?Date): DirectionType => {
  if (previousDate == null) return 'forward'
  if (isAfter(date, previousDate)) return 'forward'
  return 'backward'
}

const computeBranchId = (data, medium: string) => {
  const branches = data.branches
  return (medium !== 'phone' && !!branches && branches.length > 0 && sortByPrimaryBranch(branches)[0].id) || null
}

/**
 * load data with the provided function to load slots
 * @param date - load slots starting from that date
 * @param untilDays - if none of the slots are available, load the next slots depending on the next_start_date on the response
 * @param loadSlots - function to load the slots
 */
export const useGridData = (
  date: Date,
  untilDays = 0,
  medium: string,
  loadSlots: (date: Date) => CancelablePromise
) => {
  const [branchId, setBranchId] = useState(null)
  const [gridData, setGridData] = useState({
    data: null,
    loading: true,
    error: false,
    previousDate: null
  })

  useEffect(() => {
    const loadUntilDates = (loadDate) => {
      setGridData({
        ...gridData,
        loading: true
      })

      return loadSlots(loadDate).then(
        (map) => {
          const data = map.toJS()
          const direction = getDirection(loadDate, gridData.previousDate)
          const nextStartDate = direction === 'forward' ? data.nextStartDate : data.previousStartDate

          if (!hasEvents(data) && tryForDays(data, date, untilDays, direction) && nextStartDate) {
            return loadUntilDates(parseISO(nextStartDate))
          }

          setGridData({
            ...gridData,
            data,
            loading: false,
            error: false,
            previousDate: loadDate
          })

          if (!data.branches.find((branch) => branch.id === branchId)) setBranchId(computeBranchId(data, medium))
        },
        (_error) => {
          setGridData({ ...gridData, loading: false, error: true })
        }
      )
    }
    const promise = loadUntilDates(date)

    return () => promise.cancel()
  }, [date])

  return { ...gridData, branchId, setBranchId }
}

const availableTimesByBranches = (branches: DataBranchesType) => {
  return unique(
    flatten(
      branches.map((branch) => {
        return unique(
          flatten(
            Object.values(branch.slots).map((slots) => {
              return slots.map((slot) => {
                return formatTime(parseISO(slot.start)) + ';' + formatTime(parseISO(slot.end))
              })
            })
          )
        )
      })
    )
  )
}

const availableDatesByBranches = (branches: DataBranchesType) => {
  return unique(
    flatten(
      branches.map((branch) => {
        return Object.keys(branch.slots)
      })
    )
  )
    .map((date) => parseISO(date))
    .sort((a, b) => differenceInMilliseconds(a, b))
}

const updateHolidayStartEnd = (branches: DataBranchesType) => {
  branches.forEach((branch) => {
    branch.holidays = branch.holidays.map((holiday) => {
      return {
        ...holiday,
        start: parseISO(`${holiday.startsOn}T${holiday.openTime}`),
        end: parseISO(`${holiday.startsOn}T${holiday.closingTime}`)
      }
    })
  })
}

const getSlot = (
  branches: DataBranchesType,
  date: Date,
  startTime: string,
  endTime: string,
  lastSlot: ?SlotType,
  options: ?GridByBranchOptionsType
) => {
  const { displaySlotName } = options
  const dateFormatted = format(date, 'yyyy-MM-dd')
  const start = parseISO(`${dateFormatted}T${startTime}:00`)
  const end = parseISO(`${dateFormatted}T${endTime}:00`)

  const slots = branches.map((branch) => {
    const holiday = branch.holidays.find((holiday) => {
      return holiday.start <= start && holiday.end >= end
    })
    if (holiday) return { holiday, available: false, holidayName: holiday.name }
    const slot = branch.slots[dateFormatted].find((slot) => {
      return slot.start.substr(11, 5) === startTime && slot.end.substr(11, 5) === endTime
    })
    const available = (slot && slot.available) || false
    return { holiday: false, available, name: branch.name }
  })
  const holiday = slots.every((slot) => slot.holiday)
  const slot = slots.find((slot) => slot.available)
  return {
    holiday,
    holidayName: unique(slots.map((slot) => slot.holidayName).filter(Boolean)).join(', '),
    available: !!slot,
    name: branches.length > 1 && displaySlotName ? slot && slot.name : null,
    firstHoliday: (lastSlot === null && holiday) || (lastSlot && !lastSlot.holiday && holiday) || false,
    start,
    end
  }
}

const generateGrid = (branches, dates, times, options: ?GridByBranchOptionsType) => {
  let lastRow = null
  const grid = times.sort().map((time) => {
    const [start, end] = time.split(';')
    const row = dates.map((date, index) => {
      return getSlot(branches, date, start, end, lastRow && lastRow[index], options)
    })
    lastRow = row
    return row
  })

  grid.forEach((row, rowIndex) => {
    row.forEach((cell, columnIndex) => {
      if (cell.firstHoliday) {
        const startIndex = rowIndex
        let endIndex = grid.findIndex((row, index) => {
          return index > startIndex && row[columnIndex].holiday === false
        })
        if (endIndex === -1) endIndex = grid.length
        cell.holidayCount = endIndex - startIndex
      }
    })
  })

  return grid
}

export const gridByBranch = (data: DataType, branchId: number, options: ?GridByBranchOptionsType) => {
  return useMemo(() => {
    if (data == null) return null

    const usedBranches = branchId ? data.branches.filter((branch) => branch.id === branchId) : data.branches
    const availableDates = availableDatesByBranches(usedBranches)
    const availableTimes = availableTimesByBranches(usedBranches)
    updateHolidayStartEnd(usedBranches)

    return generateGrid(usedBranches, availableDates, availableTimes, options)
  }, [data, branchId])
}

export const getBranchValues = (data: DataType) => {
  return useMemo(() => {
    if (data == null) return null

    return sortByPrimaryBranch(data.branches).map((branch) => {
      return { value: branch.id, label: branch.name }
    })
  }, [data])
}
